import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Seo } from "@/components/Seo";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  Search,
  ArrowRight,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  author_name: string;
  author_role: string;
  category: string;
  cover_image: string;
  published_at: string;
  read_time: number;
  featured: boolean;
}

const CATEGORIES = [
  "All",
  "Industry Trends",
  "Productivity",
  "Features",
  "Security",
  "Tips & Tricks",
  "Webinars",
];

export default function Blog() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, author_name, author_role, category, cover_image, published_at, read_time, featured")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (error) throw error;
      setPosts((data as BlogPost[]) || []);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, selectedCategory]);

  const featuredPosts = posts.filter((post) => post.featured);
  const recentPosts = filteredPosts.slice(0, 9);
  const categoryLabels: Record<string, string> = {
    All: t("blog.categories.all"),
    "Industry Trends": t("blog.categories.industryTrends"),
    Productivity: t("blog.categories.productivity"),
    Features: t("blog.categories.features"),
    Security: t("blog.categories.security"),
    "Tips & Tricks": t("blog.categories.tips"),
    Webinars: t("blog.categories.webinars"),
  };

  if (loading) {
    return (
      <div className="marketing-page min-h-screen bg-background">
        <Navigation />
        <div className="pt-24 pb-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("blog.loading")}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="Blog — Insights for modern meetings | MeetHub"
        description="Playbooks, product updates, and communication strategies for teams running modern, AI-assisted video meetings."
        canonicalPath="/blog"
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:pt-32 sm:pb-16 sm:px-6 lg:px-8 bg-gradient-soft border-b relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="container mx-auto relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {t("blog.badge")}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Insights for teams running modern meetings.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-2xl">
              Actionable playbooks, product updates, and communication strategies to keep meetings efficient and outcomes-focused.
            </p>
          </div>

          <div className="max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("blog.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base rounded-full border-primary/20 focus:border-primary"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 border-b border-border bg-background/80 backdrop-blur-sm sticky top-16 z-40">
        <div className="container mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="whitespace-nowrap rounded-full"
              >
                {categoryLabels[category] ?? category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {selectedCategory === "All" && searchQuery === "" && featuredPosts.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="flex items-center gap-2 mb-8">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-bold">{t("blog.featuredArticles")}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredPosts.slice(0, 2).map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="group overflow-hidden h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-48 sm:h-56 overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                      <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                        {t("blog.featured")}
                      </Badge>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {t("blog.minRead", { count: post.read_time })}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">{categoryLabels[post.category] ?? post.category}</Badge>
                        <span className="text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          {t("blog.readMore")} <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="container mx-auto">
          <h2 className="text-2xl font-bold mb-8">
            {selectedCategory === "All" && searchQuery === ""
              ? t("blog.latestArticles")
              : t("blog.articlesFound", { count: filteredPosts.length })}
          </h2>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t("blog.noArticles")}</h3>
              <p className="text-muted-foreground mb-4">
                {posts.length === 0
                  ? t("blog.noArticlesDesc")
                  : t("blog.tryAdjusting")
                }
              </p>
              {(searchQuery || selectedCategory !== "All") && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                  }}
                >
                  {t("blog.clearFilters")}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="group overflow-hidden h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {t("blog.minRead", { count: post.read_time })}
                        </span>
                      </div>
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary text-sm font-semibold">
                            {post.author_name.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {post.author_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {post.author_role}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary/5">
        <div className="container mx-auto text-center max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            {t("blog.newsletter.title")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t("blog.newsletter.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder={t("blog.newsletter.emailPlaceholder")}
              className="h-12 rounded-full"
            />
            <Button size="lg" className="rounded-full gap-2">
              {t("blog.newsletter.subscribe")}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
